// src/components/ArticulosList.js
import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from '../components/i18n';
import logo from '../assets/LIR-BLANCO.png';
import compassLogo from '../assets/Recurso-8compass-NEGRO.png';
import LangSwitcher from '../components/LangSwitcher';
import { api, endpoints } from '../api/api';

const TOPBAR_HEIGHT = 120;

const MAX_COUNT = 1000;

const ArticulosList = ({ sujeto, lang, onSubmitted, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

    // Responsive: detectar pantalla pequeña
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [topBarHeight, setTopBarHeight] = useState(isMobile ? 160 : TOPBAR_HEIGHT);
  const topBarRef = useRef(null);
  const ctaRef = useRef(null);
  const [ctaHeight, setCtaHeight] = useState(0);


  // === Idioma: desde el slash ===
  const urlLang = location.pathname.startsWith('/en') ? 'en' : 'es';
  const effectiveLang = urlLang;
  const dict = (t && t[effectiveLang]) ? t[effectiveLang] : (t?.es || {});
  const opciones = Array.isArray(dict.ratings)
    ? dict.ratings
    : ['Outstanding', 'Excellent', 'Very Good', 'Leave fill blank'];

  // ====== Restauración sujeto ======
  const SUJETO_KEY = 'lir_sujeto';
  const [sujetoCache, setSujetoCache] = useState(null);
  const [triedRestoreSujeto, setTriedRestoreSujeto] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SUJETO_KEY);
      if (raw) setSujetoCache(JSON.parse(raw));
    } catch {}
    setTriedRestoreSujeto(true);
  }, []);

  useEffect(() => {
    try {
      if (sujeto) {
        localStorage.setItem(SUJETO_KEY, JSON.stringify(sujeto));
        setSujetoCache(sujeto);
      }
    } catch {}
  }, [sujeto]);

  const effectiveSujeto = sujeto || sujetoCache;

  useEffect(() => {
    if (!triedRestoreSujeto) return;
    if (!effectiveSujeto) {
      navigate(effectiveLang === 'en' ? '/en' : '/es', { replace: true });
    }
  }, [triedRestoreSujeto, effectiveSujeto, effectiveLang, navigate]);

  // ====== Estados ======
  const [articulos, setArticulos] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [statusById, setStatusById] = useState({});
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState('');

  // Slides
  const [index, setIndex] = useState(0);
  const [animDir, setAnimDir] = useState(0);
  const touchStartX = useRef(null);

  // Topbar: ver respuestas
  const [showAnswers, setShowAnswers] = useState(false);

   useLayoutEffect(() => {
    const measure = () => {
      const topH = topBarRef.current?.getBoundingClientRect().height || 0;
      const ctaH = ctaRef.current?.getBoundingClientRect().height || 0;
      setTopBarHeight(topH);
      setCtaHeight(ctaH);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isMobile, articulos.length, showAnswers, index]);


  // Storage por usuario
  const STORAGE_KEY = effectiveSujeto?.id ? `lir_respuestas_${effectiveSujeto.id}` : null;
  const INDEX_KEY = effectiveSujeto?.id ? `lir_index_${effectiveSujeto.id}` : null;

  // ===== Helpers =====
  const normKey = (v) => String(v ?? '').trim().toUpperCase();

  const keysForArticle = (a = {}) => {
    const ks = [a.id_articulo, a.codigo, a.code, a.clave, a.id, a.uid, a.slug]
      .map(normKey)
      .filter(Boolean);
    return Array.from(new Set(ks));
  };

  const getServerKey = (a = {}) =>
    normKey(a.id_articulo || a.codigo || a.code || a.clave || a.id);

  const normalizeArticles = (arr = []) =>
    arr.map((a) => {
      const key = getServerKey(a);
      return key && !a.id_articulo ? { ...a, id_articulo: key } : a;
    });

  const getAnswerForArticle = (a, map) => {
    for (const k of keysForArticle(a)) {
      const v = map[k];
      if (v != null && v !== '') return v;
    }
    return '';
  };

  const getStatusForArticle = (a, map) => {
    for (const k of keysForArticle(a)) {
      const v = map[k];
      if (v) return v;
    }
    return 'idle';
  };

  // Obtener comentario para un artículo
  const getCommentForArticle = (a, map) => {
    if (!a) return '';
    for (const k of keysForArticle(a)) {
      const v = map[k];
      if (typeof v === 'string' && v !== '') return v;
    }
    return '';
  };

  const toUpperKeyMap = (obj = {}) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => (out[normKey(k)] = v));
    return out;
  };

  const fetchServerRespuestas = async (idSujeto) => {
    try {
      const r = await api.get(endpoints.respuestas(idSujeto), {
        params: { _ts: Date.now() },
      });
      if (!Array.isArray(r.data)) return { calificaciones: {}, comentarios: {} };
      return r.data.reduce(
        (acc, it) => {
          if (it?.id_articulo != null) {
            const k = normKey(it.id_articulo);
            if (it.calificacion != null && it.calificacion !== '') {
              acc.calificaciones[k] = it.calificacion;
            }
            if (it.comentario != null && it.comentario !== '') {
              acc.comentarios[k] = it.comentario;
            }
          }
          return acc;
        },
        { calificaciones: {}, comentarios: {} }
      );
    } catch {
      return {};
    }
  };

  // ---- Cargar artículos ----
  useEffect(() => {
    if (!effectiveSujeto?.id) return;
    (async () => {
      try {
        const res = await api.get(endpoints.articulos(effectiveSujeto.id), {
          params: { _ts: Date.now() },
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const data = normalizeArticles(raw);
        setArticulos(data);
        if (data.length === 0) {
          setMensaje(dict.no_articles || 'No hay artículos disponibles.');
        }
      } catch {
        setMensaje(dict.error_fetch_articles || 'Error al obtener artículos.');
      }
    })();
  }, [effectiveSujeto, dict]);

  // ---- Rehidratación inicial: servidor + local ----
  useEffect(() => {
    if (!effectiveSujeto?.id) return;

    let mounted = true;
    (async () => {
      const server = await fetchServerRespuestas(effectiveSujeto.id);

      let local = {};
      let localComments = {};
      try {
        if (STORAGE_KEY) {
          const raw = localStorage.getItem(STORAGE_KEY);
          const rawComments = localStorage.getItem(`${STORAGE_KEY}_comments`);
          local = raw ? JSON.parse(raw) : {};
          localComments = rawComments ? JSON.parse(rawComments) : {};
        }
      } catch {}

      const mergedCalificaciones = {
        ...toUpperKeyMap(local),
        ...toUpperKeyMap(server.calificaciones || {}),
      };
      const mergedComentarios = {
        ...toUpperKeyMap(localComments),
        ...toUpperKeyMap(server.comentarios || {}),
      };
      if (!mounted) return;

      setRespuestas(mergedCalificaciones);
      setComentarios(mergedComentarios);
      setStatusById((s) => {
        const next = { ...s };
        Object.keys(server.calificaciones || {}).forEach((id) => {
          next[normKey(id)] = 'saved';
        });
        return next;
      });

      let restoredIndex = 0;
      try {
        if (INDEX_KEY) {
          const savedIdx = parseInt(localStorage.getItem(INDEX_KEY) || '0', 10);
          if (!Number.isNaN(savedIdx)) restoredIndex = savedIdx;
        }
      } catch {}
      setIndex(restoredIndex);
    })();

    return () => {
      mounted = false;
    };
  }, [effectiveSujeto, STORAGE_KEY, INDEX_KEY]);

  // ---- Índice válido ----
  useEffect(() => {
    if (!articulos.length) return;
    const inRange = index >= 0 && index < articulos.length;
    if (!inRange) {
      const firstUnanswered = articulos.findIndex((a) => !getAnswerForArticle(a, respuestas));
      setIndex(firstUnanswered >= 0 ? firstUnanswered : 0);
    }
  }, [articulos, index, respuestas]);

  // ---- Persistencia local ----
  useEffect(() => {
    try {
      if (STORAGE_KEY) localStorage.setItem(STORAGE_KEY, JSON.stringify(respuestas));
    } catch {}
  }, [respuestas, STORAGE_KEY]);

  useEffect(() => {
    try {
      if (STORAGE_KEY) localStorage.setItem(`${STORAGE_KEY}_comments`, JSON.stringify(comentarios));
    } catch {}
  }, [comentarios, STORAGE_KEY]);

  useEffect(() => {
    try {
      if (INDEX_KEY) localStorage.setItem(INDEX_KEY, String(index));
    } catch {}
  }, [index, INDEX_KEY]);

  // Métricas
  const completados = useMemo(
    () => articulos.filter((a) => !!getAnswerForArticle(a, respuestas)).length,
    [articulos, respuestas]
  );
  const total = articulos.length;
  const faltan = Math.max(total - completados, 0);
  const todoListo = total > 0 && completados === total;

  // ---- Guardado inmediato ----
  const saveSingle = async (id_articulo_key, calificacion, comentario) => {
    const idStr = normKey(id_articulo_key);
    setStatusById((s) => ({ ...s, [idStr]: 'saving' }));
    try {
      await api.post(endpoints.responder(), {
        id_sujeto: effectiveSujeto.id,
        id_articulo: idStr,
        calificacion,
        comentario: (comentario ?? comentarios[idStr] ?? '').slice(0, MAX_COUNT),
      });
      setStatusById((s) => ({ ...s, [idStr]: 'saved' }));
    } catch {
      setStatusById((s) => ({ ...s, [idStr]: 'error' }));
      setMensaje(dict.error_submit_single || 'No se pudo guardar una calificación. Reintentaremos al enviar.');
    }
  };

  const handleChange = (articuloKey, value) => {
    const key = normKey(articuloKey);
    setRespuestas((prev) => ({ ...prev, [key]: value }));
    if (value) saveSingle(key, value, comentarios[key]);
  };

  const handleCommentChange = (articuloKey, value) => {
    const key = normKey(articuloKey);
    setComentarios((prev) => ({ ...prev, [key]: value }));
    if (respuestas[key]) {
      setStatusById((prev) => ({ ...prev, [key]: 'dirty' }));
    }
  };

  const saveCurrentBeforeNext = async () => {
    const currentArticle = articulos[index];
    if (!currentArticle) return;

    const key = getServerKey(currentArticle);
    const calificacion = getAnswerForArticle(currentArticle, respuestas);
    if (!key || !calificacion) return;

    const comentario = getCommentForArticle(currentArticle, comentarios);
    await saveSingle(key, calificacion, comentario);
  };

  // ---- Envío final ----
  const handleSubmit = async () => {
    if (!todoListo) {
      alert(dict.must_rate_all || 'Debe calificar todos los artículos antes de enviar.');
      return;
    }
    setLoading(true);
    setMensaje('');

    const pendientes = articulos
      .map((a) => {
        const key = getServerKey(a);
        const calificacion = getAnswerForArticle(a, respuestas);
        const comentario = getCommentForArticle(a, comentarios);
        return { key, calificacion, comentario };
      })
      .filter(({ key, calificacion }) => calificacion && statusById[key] !== 'saved');

    try {
      if (pendientes.length) {
        await Promise.all(
          pendientes.map(({ key, calificacion, comentario }) =>
            api.post(endpoints.responder(), {
              id_sujeto: effectiveSujeto.id,
              id_articulo: key,
              calificacion,
              comentario: (comentario ?? '').slice(0, MAX_COUNT),
            })
          )
        );
      }
      try { sessionStorage.setItem('lir_submitted', '1'); } catch {}
      if (typeof onSubmitted === 'function') onSubmitted();
      navigate(effectiveLang === 'en' ? '/en/confirmation' : '/es/confirmacion', { replace: true });
    } catch {
      setMensaje(dict.error_submit || 'Error al enviar respuestas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('answers') === '1') setShowAnswers(true);
  }, [location.search]);

  // Navegación
  const goTo = (next) => {
    if (next < 0 || next > total - 1) return;
    setAnimDir(next > index ? 1 : -1);
    setIndex(next);
  };
  const handleLogout = () => {
    try {
      localStorage.removeItem(SUJETO_KEY);
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      if (INDEX_KEY) localStorage.removeItem(INDEX_KEY);
      sessionStorage.removeItem('sujeto');
      sessionStorage.removeItem('lir_submitted');
    } catch {}
    if (typeof onLogout === 'function') onLogout();
    navigate(effectiveLang === 'en' ? '/en' : '/es', { replace: true });
  };


  const isLast = index === total - 1;
  const canPrev = index > 0;
  const canNext = !(isLast || !getAnswerForArticle(articulos[index] || {}, respuestas));

  const [isNavigatingNext, setIsNavigatingNext] = useState(false);

  const next = async () => {
    if (!canNext || isNavigatingNext) return;

    setIsNavigatingNext(true);
    await saveCurrentBeforeNext();
    goTo(index + 1);
    setIsNavigatingNext(false);
  };
  const prev = () => { if (canPrev) goTo(index - 1); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total]);

  // Swipe
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -50) next();
    if (dx > 50) prev();
    touchStartX.current = null;
  };

  if (triedRestoreSujeto && !effectiveSujeto) return null;

  if (articulos.length === 0) {
  return (
    <main
      style={{
        paddingTop: topBarHeight + 16,
        paddingBottom: ctaHeight + 24,
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem',
        background:
          'radial-gradient(1200px 800px at 15% -10%, rgba(233,63,99,0.12) 0%, rgba(0,0,0,0) 40%), #000',
        color: '#ffffff',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* ===== Barra superior fija (con Cerrar sesión) ===== */}
      <div
        ref={topBarRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          background:
            'linear-gradient(180deg, rgba(10,10,10,.95) 0%, rgba(10,10,10,.88) 60%, rgba(10,10,10,.80) 100%)',
          borderBottom: '1px solid #2a2a2a',
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: '0 auto',
            height: '100%',
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'auto 1fr auto',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '1rem',
            padding: '0 1rem',
          }}
        >
          <img
            src={logo}
            alt="LIR Logo"
            style={{
              height: 44,
              objectFit: 'contain',
              opacity: 0.95,
              alignSelf: isMobile ? 'center' : 'auto',
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div
              style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 8,
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <h2
                style={{
                  color: '#fff',
                  fontWeight: 800,
                  margin: 0,
                  fontSize: isMobile ? '1rem' : '1.35rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {(effectiveLang === 'es' ? (dict.greet_m || 'Bienvenido') : (dict.welcome || 'Welcome'))},{' '}
                {effectiveSujeto?.nombre} {effectiveSujeto?.apellido}
              </h2>
            </div>

            <p
              style={{
                color: '#bdbdbd',
                margin: 0,
                fontSize: isMobile ? '.9rem' : '1rem',
              }}
            >
              {dict.no_articles || 'No hay artículos disponibles.'}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}
          >
            <button
              onClick={handleLogout}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid #2a2a2a',
                color: '#fff',
                borderRadius: 8,
                padding: '.35rem .6rem',
                cursor: 'pointer',
              }}
            >
              {dict.logout || 'Cerrar sesión'}
            </button>
            <LangSwitcher />
            <img
              src={compassLogo}
              alt="Compass"
              style={{ height: 44, objectFit: 'contain', opacity: 0.95 }}
            />
          </div>
        </div>
      </div>

      {/* ===== Contenido vacío centrado ===== */}
      <div
        style={{
          minHeight: '60vh',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            color: '#e5e5e5',
            maxWidth: 700,
          }}
        >
          <h3 style={{ margin: 0, fontWeight: 800 }}>
            {dict.no_articles || 'No hay artículos disponibles.'}
          </h3>
          <p style={{ color: '#bdbdbd', marginTop: 8 }}>
            {dict.error_fetch_articles_hint ||
              'Puedes cerrar sesión o volver a intentar más tarde.'}
          </p>
        </div>
      </div>
    </main>
  );
}

  // -------- Saludo con género --------
  const isSpanishUI = effectiveLang === 'es';
  const genero = (effectiveSujeto?.genero || '').toLowerCase().trim();
  const saludo = isSpanishUI
    ? (genero.startsWith('fem') ? (dict.greet_f || 'Bienvenida') : (dict.greet_m || 'Bienvenido'))
    : (dict.welcome || 'Welcome');

  const current = articulos[index] || {};
  const saved = getAnswerForArticle(current, respuestas);
  const currentComment = getCommentForArticle(current, comentarios);
  const currStatus = getStatusForArticle(current, statusById);
  const hasSavedInOptions = saved && opciones.includes(saved);

  // ------- Formatear cuerpo: HTML directo o texto plano con viñetas -------
  const renderCuerpo = (txt) => {
    if (!txt) return null;
    const s = String(txt).replace(/\r\n/g, '\n').trim();

    // Si el contenido tiene etiquetas HTML, renderizar directamente
    if (/<[a-z][\s\S]*>/i.test(s)) {
      return (
        <div
          className="article-html-body"
          dangerouslySetInnerHTML={{ __html: s }}
        />
      );
    }

    // Texto plano: divide por bloques (líneas en blanco)
    const blocks = s.split(/\n{2,}/);

    return blocks.map((block, i) => {
      const lines = block.split('\n');

      // ¿Todas las líneas del bloque son viñetas?
      const isListBlock = lines.every((l) => /^\s*[-•·]/.test(l.trim()));

      if (isListBlock) {
        return (
          <ul key={`ul-${i}`} className="article-list">
            {lines.map((l, j) => (
              <li key={`li-${i}-${j}`} className="article-li">
                {l.replace(/^\s*[-•·]\s*/, '')}
              </li>
            ))}
          </ul>
        );
      }

      // Párrafo normal, respetando \n
      return (
        <p
          key={`p-${i}`}
          className="article-paragraph"
        >
          {block}
        </p>
      );
    });
  };

  const StatusBadge = ({ st }) => {
    const base = {
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: '.75rem',
      fontWeight: 700,
      border: '1px solid',
      whiteSpace: 'nowrap',
    };
    if (st === 'saved') return <span style={{ ...base, color: '#34d399', borderColor: 'rgba(52,211,153,.5)', background: 'rgba(52,211,153,.12)' }}>✓ {dict.saved || 'Guardado'}</span>;
    if (st === 'saving') return <span style={{ ...base, color: '#bdbdbd', borderColor: 'rgba(189,189,189,.5)', background: 'rgba(189,189,189,.12)' }}>{dict.saving || 'Guardando…'}</span>;
    if (st === 'error') return <span style={{ ...base, color: '#ff6b6b', borderColor: 'rgba(255,107,107,.5)', background: 'rgba(255,107,107,.12)' }}>{dict.retry_later || 'Error'}</span>;
    return <span style={{ ...base, color: '#bdbdbd', borderColor: 'rgba(189,189,189,.35)', background: 'rgba(189,189,189,.08)' }}>—</span>;
  };

  return (
    <main
      style={{
        paddingTop: topBarHeight + 16,
        paddingBottom: ctaHeight + 24,
        paddingLeft: '1.25rem',
        paddingRight: '1.25rem',
        background:
          'radial-gradient(1200px 800px at 15% -10%, rgba(233,63,99,0.12) 0%, rgba(0,0,0,0) 40%), #000',
        color: '#ffffff',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* ===== Barra superior fija ===== */}
      <div
        ref={topBarRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          
          zIndex: 20,
          background:
            'linear-gradient(180deg, rgba(10,10,10,.95) 0%, rgba(10,10,10,.88) 60%, rgba(10,10,10,.80) 100%)',
          borderBottom: '1px solid #2a2a2a',
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: '0 auto',
            height: '100%',
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'auto 1fr auto',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: '1rem',
            padding: '0 1rem',
          }}
        >
          <img
            src={logo}
            alt="LIR Logo"
            style={{
              height: 44,
              objectFit: 'contain',
              opacity: 0.95,
              alignSelf: isMobile ? 'center' : 'auto',
            }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div
              style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 8,
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              <h2
                style={{
                  color: '#fff',
                  fontWeight: 800,
                  margin: 0,
                  fontSize: isMobile ? '1rem' : '1.35rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {saludo}, {effectiveSujeto?.nombre} {effectiveSujeto?.apellido}
              </h2>

              {articulos.length > 0 && (
                <button
                  onClick={() => setShowAnswers((v) => !v)}
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid #2a2a2a',
                    color: '#fff',
                    fontSize: '.85rem',
                    padding: '.35rem .6rem',
                    borderRadius: 8,
                    cursor: 'pointer',
                    alignSelf: isMobile ? 'flex-start' : 'center',
                    marginTop: isMobile ? 4 : 0,
                  }}
                >
                  {showAnswers ? (dict.hide_answers || 'Ocultar respuestas') : (dict.show_answers || 'Ver respuestas')}
                </button>
              )}
            </div>

            <p
              style={{
                color: '#bdbdbd',
                margin: 0,
                fontSize: isMobile ? '.85rem' : '1rem',
                textAlign: isMobile ? 'center' : 'left',
              }}
            >
              {(dict.progress || 'Progreso')}: <strong>{completados}</strong> / {total}{' '}
              {faltan > 0 && `· ${(dict.remaining || 'Faltan')} ${faltan}`}
            </p>

            {total > 0 && (
              <div
                aria-hidden
                style={{
                  height: 6,
                  background: '#1a1a1a',
                  borderRadius: 999,
                  overflow: 'hidden',
                  border: '1px solid #2a2a2a',
                  maxWidth: 1000,
                  width: '100%',
                  marginTop: isMobile ? 4 : 6,
                }}
              >
                <div
                  style={{
                    width: `${(completados / total) * 100}%`,
                    height: '100%',
                    background: '#E93F63',
                    transition: 'width .25s ease',
                  }}
                />
              </div>
            )}
          </div>

          {/* Derecha: selección de artículos (si aplica) + cerrar sesión + selector + logo */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: isMobile ? 'center' : 'flex-end',
            }}
          >
            {effectiveSujeto?.IH_Pro && (
              <button
                onClick={() =>
                  navigate(
                    effectiveLang === 'en'
                      ? '/en/select-articles'
                      : '/es/seleccionar-articulos'
                  )
                }
                style={{
                  background: 'rgba(255,255,255,.08)',
                  border: '1px solid #2a2a2a',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '.35rem .6rem',
                  cursor: 'pointer',
                }}
              >
                {dict.select_articles || 'Seleccionar artículos'}
              </button>
            )}
            <button
              onClick={handleLogout}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid #2a2a2a',
                color: '#fff',
                borderRadius: 8,
                padding: '.35rem .6rem',
                cursor: 'pointer',
              }}
            >
              {dict.logout || 'Cerrar sesión'}
            </button>
            <LangSwitcher />
            <img
              src={compassLogo}
              alt="Compass"
              style={{ height: 44, objectFit: 'contain', opacity: 0.95 }}
            />
          </div>
        </div>
      </div>
              
        <div
          style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,              
                transform: `translateX(${animDir * 4}px)`,
                borderRadius: 10,
                boxShadow: '0 6px 16px rgba(0,0,0,.35)',
                margin: '0 auto .75rem',
                maxWidth: 980,
                padding: '1.0rem',                  
              }}
            >
              <span style={{ color: '#ffffff', fontSize: '.9rem', fontWeight: 800 }}>{dict.area || 'Area'}</span>
              <h4 style={{ color: '#bdbdbd', fontSize: '1.0rem', margin: 0, fontWeight: 600 }}>
                {current?.area ?? '—'}
              </h4>
            </div>
        </div>
          
        {/* ===== Carrusel ===== */}
        <div
          style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
        <div
          key={getServerKey(current) || `slide-${index}`}
          style={{
            border: '1px solid #2a2a2a',
            borderRadius: 12,
            padding: '1.4rem',
            margin: '0 auto',
            backgroundColor: '#1a1a1a',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
            maxWidth: 860,
            transform: `translateX(${animDir * 4}px)`,
            opacity: 1,
            animation: 'slideIn .25s ease',
          }}
          onAnimationEnd={() => setAnimDir(0)}
        >
          <h3 style={{ color: '#ffffff', fontSize: '1.15rem', margin: 0, fontWeight: 700 }}>
            {(index + 1)}. {current.titulo}
          </h3>

          {/* CUERPO FORMATEADO */}
          <div className="article-body" style={{ color: '#d0d0d0', fontSize: '1rem', marginTop: '.75rem' }}>
            {renderCuerpo(current.cuerpo)}
          </div>

          <div style={{ position: 'relative', maxWidth: 480, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ position: 'relative', flex: '1 1 auto' }}>
              <select
                aria-label={dict.select_rating || 'Seleccione una calificación'}
                onChange={(e) => handleChange(getServerKey(current), e.target.value)}
                value={saved}
                style={{
                  padding: '0.75rem 2.3rem 0.75rem 0.9rem',
                  backgroundColor: '#0f0f0f',
                  color: '#ffffff',
                  border: '1px solid #555',
                  borderRadius: 10,
                  width: '100%',
                  marginTop: '1.1rem',
                  fontSize: '1rem',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  outline: 'none',
                  transition: 'box-shadow .2s ease, border-color .2s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(233,63,99,.35)';
                  e.currentTarget.style.borderColor = '#E93F63';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#555';
                }}
              >
                <option value="" disabled style={{ backgroundColor: '#1c1c1c', color: '#808080' }}>
                  {dict.select_rating || 'Seleccione una calificación'}
                </option>

                {!hasSavedInOptions && saved && (
                  <option value={saved} style={{ backgroundColor: '#1c1c1c', color: '#fff' }}>
                    {saved}
                  </option>
                )}

                {opciones.map((opt) => (
                  <option key={opt} value={opt} style={{ backgroundColor: '#1c1c1c', color: '#fff' }}>
                    {opt}
                  </option>
                ))}
              </select>
              <span
                aria-hidden
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', opacity: 0.6 }}
              >
                ▾
              </span>
            </div>

            <div aria-live="polite" style={{ marginTop: '1.1rem', minWidth: 90, textAlign: 'left', fontSize: '.9rem' }}>
              <StatusBadge st={currStatus} />
            </div>
          </div>
        </div>

        {/* COMENTARIOS (fuera del box del artículo) */}
       {/* === SECCIÓN COMENTARIOS (NUEVO DISEÑO) === */}
<div style={{ maxWidth: 860, margin: '1.5rem auto 0', padding: '0 12px' }}>
  <div
    style={{
      border: '1px solid #2a2a2a',
      borderRadius: 14,
      padding: '1.1rem',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
      boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
    }}
  >
    {/* Header */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
      }}
    >
      <div>
      <div className='comment' style={{ color: '#fff', fontWeight: 800, fontSize: '1rem' }}>
      {dict.comment || 'Comentario'}
      {' '} 
      <span className='comment_optional' style={{ color: '#9e9e9e', fontWeight: 500 }}>
        {dict.comment_optional || '(optional)'} 
      </span>
    </div>
        <div style={{ color: '#bdbdbd', fontSize: '.85rem', marginTop: 4 }}>
         {dict.suggestionarticle || 'Sugerencias claras, observaciones o mejoras sobre el artículo'} 
        </div>
      </div>

      <div style={{ color: '#9e9e9e', fontSize: '.85rem', fontWeight: 600 }}>
        {(currentComment || '').length}/{MAX_COUNT}
      </div>
    </div>

    {/* Textarea */}
    <textarea
      id={`article-comment-${getServerKey(current)}`}
      value={currentComment}
      maxLength={1000}
      onChange={(e) =>
        handleCommentChange(getServerKey(current), e.target.value)      
      }
      

      className='placeholder_section_comment'
      placeholder={dict.placeholder_section_comment || 'Escribe un comentario sobre este articulo'} 
      style={{
        width: '100%',
        minHeight: 170,
        backgroundColor: 'rgba(0,0,0,0.4)',
        color: '#ffffff',
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.15)',
        padding: '0.95rem 1rem',
        boxSizing: 'border-box',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        resize: 'vertical',
        outline: 'none',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#E93F63';
        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(233,63,99,.25)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    />

    {/* Footer */}
    <div
      style={{
        marginTop: 8,
        color: '#9e9e9e',
        fontSize: '.8rem',
      }}
    >
      
      {dict.comment_personal_sensibility || 'No incluyas datos personales o sensibles'} 
    </div>
  </div>
</div>


        {total > 1 && (
          <div aria-label={dict.pagination || 'Paginación'} style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            {articulos.map((_, i) => (
              <button
                key={`dot-${i}`}
                onClick={() => goTo(i)}
                aria-label={`${dict.go_to || 'Ir a'} ${i + 1}`}
                style={{ width: i === index ? 22 : 12, height: 12, borderRadius: 999, background: i === index ? '#E93F63' : '#444', border: 'none', cursor: 'pointer', transition: 'all .2s ease' }}
              />
            ))}
          </div>
        )}
      </div>
      

      {/* ===== CTA inferior ===== */}
      <div
        ref={ctaRef}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(10,10,10,.85) 20%, rgba(10,10,10,.95) 100%)',
          borderTop: '1px solid #2a2a2a',
          zIndex: 10,
          backdropFilter: 'blur(6px)',
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: '0 auto',
            padding: '0.75rem 1rem',
            display: isMobile ? 'flex' : 'grid',
            flexDirection: isMobile ? 'column' : undefined,
            gridTemplateColumns: isMobile ? undefined : 'auto 1fr auto',
            alignItems: 'center',
            columnGap: isMobile ? undefined : '0.75rem',
            rowGap: isMobile ? '0.75rem' : undefined,
          }}
        >
          <button
            onClick={prev}
            disabled={!canPrev}
            style={{
              backgroundColor: canPrev ? '#E93F63' : '#bdbdbd',
              color: 'white',
              border: 'none',
              padding: '0.6rem 0.9rem',
              borderRadius: 10,
              fontWeight: 800,
              cursor: canPrev ? 'pointer' : 'not-allowed',
              fontSize: '0.95rem',
              transition: 'background-color .25s ease, transform .1s ease',
              boxShadow: canPrev ? '0 6px 16px rgba(233,63,99,.35)' : 'none',
              justifySelf: 'start',
              width: isMobile ? '100%' : undefined,
            }}
          >
            {dict.prev || 'Anterior'}
          </button>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              width: isMobile ? '100%' : 'auto',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            
            {!isLast && (
              <button
                onClick={next}
                disabled={!canNext || isNavigatingNext}
                style={{
                  backgroundColor: canNext && !isNavigatingNext ? '#E93F63' : '#bdbdbd',
                  color: 'white',
                  border: 'none',
                  padding: '0.6rem 0.9rem',
                  borderRadius: 10,
                  fontWeight: 800,
                  cursor: canNext && !isNavigatingNext ? 'pointer' : 'not-allowed',
                  fontSize: '0.95rem',
                  transition: 'background-color .25s ease, transform .1s ease',
                  boxShadow: canNext && !isNavigatingNext ? '0 6px 16px rgba(233,63,99,.35)' : 'none',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                {isNavigatingNext ? (dict.saving || 'Guardando…') : (dict.next || 'Siguiente')}
              </button>
            )}
          </div>

           <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              flexWrap: 'wrap',
              width: isMobile ? '100%' : 'auto',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <span style={{ color: '#bdbdbd', fontSize: '.95rem', textAlign: isMobile ? 'center' : 'left' }}>
              {todoListo
                ? (dict.ready_to_send || 'Listo para enviar')
                : `${dict.remaining || 'Faltan'} ${faltan} ${dict.of || 'de'} ${total}`}
            </span>
            <button
              onClick={handleSubmit}
              disabled={loading || !todoListo}
              style={{
                backgroundColor: todoListo ? '#E93F63' : '#bdbdbd',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.2rem',
                borderRadius: 10,
                fontWeight: 800,
                cursor: loading || !todoListo ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                transition: 'background-color .25s ease, transform .1s ease',
                boxShadow: todoListo ? '0 6px 16px rgba(233,63,99,.35)' : 'none',
                justifySelf: 'end',
                width: isMobile ? '100%' : undefined,
              }}
            >
              {loading
                ? (dict.sending || 'Enviando…')
                : (dict.send_ratings || 'Enviar calificaciones')}
            </button>
          </div>
        </div>
      </div>

      {/* ===== Panel "Ver respuestas" ===== */}
      {showAnswers && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 68,
            margin: '0 auto',
            maxWidth: 1120,
            background: '#111',
            border: '1px solid #2a2a2a',
            borderRadius: 12,
            boxShadow: '0 16px 40px rgba(0,0,0,.5)',
            padding: '1rem',
            zIndex: 12,
            maxHeight: '60vh',
            overflow: 'auto',
            animation: 'fadeIn .2s ease',
          }}
        >
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h4 style={{ margin: 0, color: '#fff' }}>{dict.current_answers || 'Respuestas actuales'}</h4>
            <button
              onClick={() => setShowAnswers(false)}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid #2a2a2a',
                color: '#fff',
                borderRadius: 8,
                padding: '.35rem .6rem',
                cursor: 'pointer',
              }}
            >
              {dict.close || 'Cerrar'}
            </button>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {articulos.map((a, i) => {
              const val = getAnswerForArticle(a, respuestas);
              const st = getStatusForArticle(a, statusById);
              return (
                <div
                  key={getServerKey(a) || i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '.6rem .75rem',
                    border: '1px solid #2a2a2a',
                    borderRadius: 10,
                    background: '#161616',
                  }}
                >
                  <div style={{ color: '#e5e5e5', fontWeight: 600, lineHeight: 1.35 }}>
                    {i + 1}. {a.titulo}
                    <div style={{ color: '#bdbdbd', fontSize: '.9rem', marginTop: 2 }}>
                      {val ? `${dict.rating || 'Calificación'}: ${val}` : (dict.missing || 'Sin calificar')}
                    </div>
                  </div>

                  <StatusBadge st={st} />

                  <button
                    onClick={() => { setShowAnswers(false); goTo(i); }}
                    style={{
                      background: 'rgba(233,63,99,.15)',
                      border: '1px solid rgba(233,63,99,.45)',
                      color: '#fff',
                      padding: '.4rem .7rem',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    {dict.go_to || 'Ir a'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toast solo para errores */}
      {mensaje && (
        <div
          role="status"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 86,
            background: 'rgba(255,68,68,.15)',
            border: '1px solid rgba(255,68,68,.35)',
            color: '#ff4444',
            padding: '.75rem 1rem',
            borderRadius: 12,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn .25s ease',
            zIndex: 15,
          }}
        >
          {mensaje}
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn{from{opacity:0; transform:translateY(4px)}to{opacity:1; transform:translateY(0)}}
          @keyframes slideIn{
            from{opacity:0; transform:translateX(${animDir * 20}px)}
            to{opacity:1; transform:translateX(0)}
          }
          select option { background-color:#1c1c1c; color:#fff; }
          select option:hover, select option:checked { background-color:#E93F63; color:#fff; }

          /* ===== Estilos para el cuerpo del artículo ===== */
          .article-body { text-align: justify; }
          .article-paragraph {
            margin: 0.5rem 0;
            line-height: 1.7;
            white-space: pre-line;
          }
          .article-list {
            list-style: disc;
            margin: 0.5rem 0 0.75rem 1.25rem;
            padding-left: 1rem;
          }
          .article-li {
            margin: 0.25rem 0;
            line-height: 1.6;
          }

          /* ===== Estilos para contenido HTML en artículos ===== */
          .article-html-body { text-align: justify; line-height: 1.7; }
          .article-html-body h1,
          .article-html-body h2,
          .article-html-body h3,
          .article-html-body h4,
          .article-html-body h5,
          .article-html-body h6 {
            color: #ffffff;
            margin: 1rem 0 0.5rem;
            font-weight: 700;
            line-height: 1.3;
          }
          .article-html-body h1 { font-size: 1.4rem; }
          .article-html-body h2 { font-size: 1.2rem; }
          .article-html-body h3 { font-size: 1.05rem; }
          .article-html-body p  { margin: 0.5rem 0; }
          .article-html-body ul,
          .article-html-body ol {
            margin: 0.5rem 0 0.75rem 1.25rem;
            padding-left: 1rem;
          }
          .article-html-body ul { list-style: disc; }
          .article-html-body ol { list-style: decimal; }
          .article-html-body li { margin: 0.25rem 0; line-height: 1.6; }
          .article-html-body a  { color: #E93F63; text-decoration: underline; }
          .article-html-body strong,
          .article-html-body b   { color: #ffffff; font-weight: 700; }
          .article-html-body em,
          .article-html-body i   { font-style: italic; }
          .article-html-body blockquote {
            border-left: 3px solid #E93F63;
            margin: 0.75rem 0;
            padding: 0.4rem 1rem;
            color: #aaa;
            font-style: italic;
          }
          .article-html-body table {
            border-collapse: collapse;
            width: 100%;
            margin: 0.75rem 0;
            font-size: 0.9rem;
            color: #d0d0d0;
          }
          .article-html-body th,
          .article-html-body td {
            border: 1px solid #444;
            padding: 0.5rem 0.75rem;
            text-align: left;
            vertical-align: top;
          }
          .article-html-body th {
            background-color: #2a2a2a;
            color: #ffffff;
            font-weight: 700;
          }
          .article-html-body tr:nth-child(even) td {
            background-color: #1a1a1a;
          }
        `}
      </style>
    </main>
  );
};

export default ArticulosList;
